import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLoader } from "@/contexts/LoaderContext";
import { useJourney } from "@/contexts/JourneyContext";
import { AlertCircle, CheckCircle2, Loader2, FileSearch } from "lucide-react";
import JourneyHeader from "@/components/JourneyHeader";

const MAS_DIGIO_JOURNEY_STAGE = "MAS_DIGIO_JOURNEY";
const SESSION_KEY = "mas_digilocker_request_id";
const FETCH_TIMEOUT_SECONDS = 45;

const FETCHING_MESSAGES = [
    "Connecting to DigiLocker...",
    "Fetching your Aadhaar document...",
    "Fetching your PAN document...",
    "Verifying document authenticity...",
    "Securely storing your documents...",
    "Almost done, please wait...",
];

const MasDigioJourney = () => {
    const navigate = useNavigate();
    const { state: journeyState, refresh: refreshJourney } = useJourney();
    const [consentChecked, setConsentChecked] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [requestId, setRequestId] = useState<number | null>(null);
    const [status, setStatus] = useState<'idle' | 'initiated' | 'fetching' | 'completed' | 'failed'>('idle');

    // stageChecked: true after mount-time refreshJourney() resolves
    const [stageChecked, setStageChecked] = useState(false);

    const [fetchCountdown, setFetchCountdown] = useState(FETCH_TIMEOUT_SECONDS);
    const [msgIndex, setMsgIndex] = useState(0);

    const { setLoading } = useLoader();
    const pollingActive = useRef(false);
    const isDone = useRef(false);
    const popupRef = useRef<Window | null>(null);
    const navigateRef = useRef(navigate);
    const setLoadingRef = useRef(setLoading);
    const refreshJourneyRef = useRef(refreshJourney);
    const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const msgIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => { navigateRef.current = navigate; }, [navigate]);
    useEffect(() => { setLoadingRef.current = setLoading; }, [setLoading]);
    useEffect(() => { refreshJourneyRef.current = refreshJourney; }, [refreshJourney]);

    // ── Navigate to next page after completion ────────────────────────────────
    // Accepts an optional hint URL from the API response; if missing, fetches
    // the journey state directly (POST /api/journey/state/) to get the
    // authoritative next route.
    const navigateToNext = useCallback(async (hintUrl?: string | null) => {
        if (hintUrl) {
            navigateRef.current(hintUrl);
            return;
        }
        // No hint — fetch fresh journey state to get the latest last_route
        try {
            const r = await axios.post(`${API_BASE_URL}/api/journey/state/`);
            const route = r.data?.last_route || "/dashboard";
            navigateRef.current(route);
        } catch (_) {
            navigateRef.current("/dashboard");
        }
    }, []); // stable — uses only refs

    // ── Poll backend for DigiLocker status ────────────────────────────────────
    const pollStatus = useCallback((reqId: number) => {
        if (pollingActive.current || isDone.current) return;

        pollingActive.current = true;
        axios.get(`${API_BASE_URL}/api/mas/digilocker/status/${reqId}/`)
            .then(async (response) => {
                const data = response.data;
                pollingActive.current = false;

                if (data.success && data.data?.status === "completed") {
                    isDone.current = true;
                    sessionStorage.removeItem(SESSION_KEY);
                    setStatus('completed');
                    setProcessing(false);
                    setLoadingRef.current(false);

                    // Close any open popup
                    if (popupRef.current && !popupRef.current.closed) {
                        try { popupRef.current.close(); } catch (_) { /* ignore */ }
                    }

                    // Wait 1.5s to show the success screen, then navigate
                    await new Promise((r) => setTimeout(r, 1500));
                    await navigateToNext(data.next_page_url);

                } else if (data.success && data.data?.status === "failed") {
                    isDone.current = true;
                    sessionStorage.removeItem(SESSION_KEY);
                    setStatus('failed');
                    setError("Verification failed. Please try again.");
                    setProcessing(false);
                    setLoadingRef.current(false);

                } else {
                    // Still pending — keep polling every 5 seconds
                    setTimeout(() => pollStatus(reqId), 5000);
                }
            })
            .catch((err) => {
                console.error("MasDigioJourney: polling error:", err);
                pollingActive.current = false;
                setTimeout(() => pollStatus(reqId), 5000);
            });
    }, [navigateToNext]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── On mount: refresh journey, then decide what to do ────────────────────
    useEffect(() => {
        refreshJourney().finally(() => setStageChecked(true));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── After fresh journey state: redirect if already past this stage ────────
    useEffect(() => {
        if (!stageChecked || !journeyState) return;
        if (journeyState.current_stage !== MAS_DIGIO_JOURNEY_STAGE) {
            sessionStorage.removeItem(SESSION_KEY);
            const nextRoute = journeyState.last_route || "/dashboard";
            console.log(
                `MasDigioJourney: stage already advanced to ${journeyState.current_stage} — redirecting to ${nextRoute}`
            );
            navigate(nextRoute, { replace: true });
            return;
        }

        // Still at MAS_DIGIO_JOURNEY — check if we need to resume a prior session
        if (isDone.current) return;

        // 1. Try sessionStorage first (fastest path)
        const savedReqId = sessionStorage.getItem(SESSION_KEY);
        if (savedReqId) {
            const parsed = parseInt(savedReqId, 10);
            if (!isNaN(parsed)) {
                console.log(`MasDigioJourney: restored requestId=${parsed} from sessionStorage`);
                setRequestId(parsed);
                setStatus('fetching');
                pollStatus(parsed);
                return;
            }
        }

        // 2. Check URL query params (MAS may append requestId to redirectUrl)
        const params = new URLSearchParams(window.location.search);
        const qpReqId = params.get('requestId') || params.get('request_id');
        if (qpReqId) {
            const parsed = parseInt(qpReqId, 10);
            if (!isNaN(parsed)) {
                console.log(`MasDigioJourney: requestId=${parsed} found in URL query params`);
                sessionStorage.setItem(SESSION_KEY, String(parsed));
                setRequestId(parsed);
                setStatus('fetching');
                pollStatus(parsed);
                return;
            }
        }

        // 3. Ask backend for the latest pending request (handles cleared sessionStorage
        //    after mobile cross-origin redirect)
        axios.get(`${API_BASE_URL}/api/mas/digilocker/pending/`)
            .then((res) => {
                const pendingId: number | null = res.data?.request_id ?? null;
                if (pendingId && !isDone.current) {
                    console.log(`MasDigioJourney: recovered requestId=${pendingId} from /pending/`);
                    sessionStorage.setItem(SESSION_KEY, String(pendingId));
                    setRequestId(pendingId);
                    setStatus('fetching');
                    pollStatus(pendingId);
                }
                // null → no pending request, stay idle (show consent screen)
            })
            .catch((err) => {
                console.warn("MasDigioJourney: /pending/ lookup failed:", err);
                // Non-fatal — user can start fresh
            });

    }, [stageChecked]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Countdown + rotating messages while fetching ──────────────────────────
    useEffect(() => {
        if (status !== 'fetching') {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            if (msgIntervalRef.current) clearInterval(msgIntervalRef.current);
            return;
        }

        setFetchCountdown(FETCH_TIMEOUT_SECONDS);
        setMsgIndex(0);

        countdownIntervalRef.current = setInterval(() => {
            setFetchCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownIntervalRef.current!);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        msgIntervalRef.current = setInterval(() => {
            setMsgIndex((prev) => (prev + 1) % FETCHING_MESSAGES.length);
        }, 6000);

        return () => {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            if (msgIntervalRef.current) clearInterval(msgIntervalRef.current);
        };
    }, [status]);

    // ── Desktop: re-trigger poll when user returns to this tab ───────────────
    useEffect(() => {
        const handleFocus = () => {
            if (!isDone.current && requestId) {
                console.log("MasDigioJourney: tab focused, checking DigiLocker status...");
                if (status === 'initiated') {
                    setStatus('fetching');
                }
                pollStatus(requestId);
            }
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [requestId, status, pollStatus]);

    // ── Start verification ────────────────────────────────────────────────────
    const handleStartVerification = async () => {
        setError(null);
        setProcessing(true);
        setLoading(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/mas/digilocker/init/`);
            if (response.data.success) {
                const { consentUrl, requestId: reqId } = response.data.data;

                // Persist requestId BEFORE navigating away (mobile same-tab redirect)
                sessionStorage.setItem(SESSION_KEY, String(reqId));
                setRequestId(reqId);

                const popup = window.open(consentUrl, "_blank");
                popupRef.current = popup;

                if (!popup || popup.closed || typeof popup.closed === 'undefined') {
                    // Mobile: same-tab navigation. sessionStorage is set; when the
                    // user returns to /mas-digio-journey the stageChecked effect will
                    // pick up the sessionStorage key and resume polling.
                    window.location.href = consentUrl;
                    return;
                }

                // Desktop: popup opened — wait for it to close, then start fetching
                setStatus('initiated');
                setLoading(false);

                const timer = setInterval(() => {
                    if (popup.closed) {
                        clearInterval(timer);
                        setStatus('fetching');
                        pollStatus(reqId);
                    }
                }, 1000);

            } else {
                throw new Error("Failed to initiate verification");
            }
        } catch (err: any) {
            console.error(err);
            setError(err.message || "Something went wrong. Please try again.");
            setProcessing(false);
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-intellend-white">
            <JourneyHeader />

            <main className="container mx-auto px-4 py-8 max-w-2xl">
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                    <h1 className="text-2xl font-bold text-gray-800 mb-6">
                        DigiLocker Verification
                    </h1>

                    {/* ── IDLE: consent + start button ── */}
                    {status === 'idle' && (
                        <div className="space-y-6">
                            <p className="text-gray-600">
                                To proceed with your loan application, we need to verify your identity documents via DigiLocker.
                                This is a secure and paperless process.
                            </p>

                            <div className="bg-blue-50 p-4 rounded-lg flex gap-3">
                                <AlertCircle className="text-blue-500 flex-shrink-0" />
                                <p className="text-sm text-blue-700">
                                    You will be redirected to the DigiLocker website to provide your consent and fetch your Aadhaar and PAN documents.
                                </p>
                            </div>

                            <label className="flex items-start gap-3 cursor-pointer group" htmlFor="consent-checkbox">
                                <input
                                    id="consent-checkbox"
                                    type="checkbox"
                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-intellend-green focus:ring-intellend-green"
                                    checked={consentChecked}
                                    onChange={(e) => setConsentChecked(e.target.checked)}
                                />
                                <span className="text-sm text-gray-700 select-none">
                                    I hereby provide my consent to fetch my digital documents from DigiLocker for the purpose of loan application verification.
                                </span>
                            </label>

                            <Button
                                onClick={handleStartVerification}
                                disabled={!consentChecked || processing}
                                className="w-full bg-intellend-green hover:bg-intellend-green/90 text-white font-semibold py-6 rounded-lg transition-all"
                            >
                                {processing ? "Processing..." : "Start Digital Verification"}
                            </Button>
                        </div>
                    )}

                    {/* ── INITIATED: popup just opened ── */}
                    {status === 'initiated' && (
                        <div className="text-center py-10 space-y-4">
                            <div className="flex justify-center">
                                <Loader2 className="h-12 w-12 text-intellend-green animate-spin" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-800">Opening DigiLocker</h2>
                            <p className="text-gray-600">
                                A DigiLocker window has been opened. Please complete the verification there.
                            </p>
                            <p className="text-sm text-gray-400">
                                This page will automatically update once you finish.
                            </p>
                        </div>
                    )}

                    {/* ── FETCHING: countdown loading screen ── */}
                    {status === 'fetching' && (
                        <div className="text-center py-10 space-y-6">
                            <div className="flex justify-center">
                                <div className="relative">
                                    <div className="p-4 bg-blue-50 rounded-full">
                                        <FileSearch className="h-12 w-12 text-intellend-green" />
                                    </div>
                                    <span className="absolute -top-1 -right-1 flex h-4 w-4">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-intellend-green opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-4 w-4 bg-intellend-green"></span>
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <h2 className="text-2xl font-bold text-gray-800">Fetching Your Documents</h2>
                                <p className="text-gray-600 text-base">
                                    We are retrieving your Aadhaar and PAN documents from DigiLocker.
                                    This usually takes 30–45 seconds.
                                </p>
                            </div>

                            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                                <p className="text-sm text-blue-700 font-medium">
                                    {FETCHING_MESSAGES[msgIndex]}
                                </p>
                            </div>

                            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-intellend-green h-2 rounded-full transition-all duration-1000"
                                    style={{
                                        width: `${Math.max(5, ((FETCH_TIMEOUT_SECONDS - fetchCountdown) / FETCH_TIMEOUT_SECONDS) * 100)}%`
                                    }}
                                />
                            </div>

                            <p className="text-sm text-gray-400">
                                {fetchCountdown > 0
                                    ? `Please wait… (${fetchCountdown}s)`
                                    : "Still working, please hold on..."}
                            </p>

                            <p className="text-xs text-gray-400">
                                Please do not close or refresh this page.
                            </p>
                        </div>
                    )}

                    {/* ── COMPLETED ── */}
                    {status === 'completed' && (
                        <div className="text-center py-12 space-y-6">
                            <div className="flex justify-center">
                                <div className="p-4 bg-green-100 rounded-full">
                                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Verification Successful</h2>
                            <p className="text-gray-600">
                                Your documents have been fetched and verified. Moving to the next step...
                            </p>
                        </div>
                    )}

                    {/* ── FAILED ── */}
                    {status === 'failed' && (
                        <div className="text-center py-10 space-y-6">
                            <div className="flex justify-center">
                                <div className="p-4 bg-red-100 rounded-full">
                                    <AlertCircle className="h-12 w-12 text-red-500" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Verification Failed</h2>
                            <p className="text-gray-500">
                                We could not fetch your documents from DigiLocker. Please try again.
                            </p>
                            <Button
                                onClick={() => {
                                    setStatus('idle');
                                    setError(null);
                                    isDone.current = false;
                                    pollingActive.current = false;
                                    sessionStorage.removeItem(SESSION_KEY);
                                }}
                                className="w-full bg-intellend-green hover:bg-intellend-green/90 text-white font-semibold py-4 rounded-lg"
                            >
                                Try Again
                            </Button>
                        </div>
                    )}

                    {/* ── Error banner ── */}
                    {error && status !== 'failed' && (
                        <div className="mt-6 p-4 bg-red-50 rounded-lg flex gap-3 border border-red-100">
                            <AlertCircle className="text-red-500 flex-shrink-0" />
                            <p className="text-sm text-red-700 font-medium">{error}</p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default MasDigioJourney;